import React, { useEffect, useRef, useState } from 'react';
import '../styles/VerseDisplay.css';

// Libellé de l'unité du Coran testée par la question
const UNIT_LABELS = {
  hizb: 'Hizb',
  juz: 'Juz',
  page: 'Page',
  surah: 'Sourate'
};

// Masque affiché à la place d'un mot pas encore récité, identique à celui de
// RecitationAnswer pour que les deux vues se lisent de la même façon.
const PENDING_MASK = '•••';

// Silhouette de récitant : SVG en ligne, donc aucune requête réseau et un
// rendu net à toute taille. `currentColor` la fait suivre l'état du bouton.
const ReciterIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
    <circle cx="10" cy="7" r="3.2" fill="currentColor" />
    <path
      d="M4.2 19.2c0-3.2 2.6-5.4 5.8-5.4s5.8 2.2 5.8 5.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    {/* Ondes sonores : la récitation qui sort */}
    <path
      d="M17.6 8.4a4 4 0 0 1 0 5.2M20 6.4a7 7 0 0 1 0 9.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const VerseDisplay = ({
  verse,
  isLoading,
  examInfo = null,
  recitationProgress = null,
  recitationMasked = false,
}) => {
  const [showTranslation, setShowTranslation] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.currentTime = 0;
    setIsPlaying(false);
  }, [verse?.surah, verse?.numberInSurah]);

  const playVerseAudio = async () => {
    if (!audioRef.current) return;
    try {
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      setIsPlaying(false);
    }
  };

  const activeReference = recitationMasked && recitationProgress?.reference
    ? recitationProgress.reference
    : null;
  // La question affichée est FIGÉE : ni son texte ni son numéro ne changent
  // pendant la récitation. L'affichage progressif ne s'applique donc qu'au cas
  // où le verset récité est précisément celui montré (mode révision). En mode
  // examen on récite le verset suivant : la question reste intacte à l'écran et
  // la correction mot à mot se lit dans le panneau de réponse.
  const isCurrentQuestion = Boolean(activeReference)
    && activeReference.surah === verse?.surah
    && activeReference.ayah === verse?.numberInSurah;
  const progressiveWords = isCurrentQuestion && Array.isArray(recitationProgress?.words)
    ? recitationProgress.words
    : null;
  // Progression cumulée transmise par le composant de récitation : elle
  // survit à un arrêt, ce qui permet de reprendre sans tout re-dévoiler.
  const revealedSet = Array.isArray(recitationProgress?.revealed)
    ? new Set(recitationProgress.revealed)
    : null;
  const displaySurah = verse?.surah;
  const displayAyah = verse?.numberInSurah;
  const displayArabic = verse?.arabic;

  const toggleTranslation = () => {
    setShowTranslation(!showTranslation);
  };
  // Afficher un état de chargement
  if (isLoading) {
    return (
      <div className="verse-display loading">
        <div className="loading-spinner"></div>
        <p>Chargement du verset...</p>
      </div>
    );
  }
  
  // Aucun verset n'est encore sélectionné
  if (!verse) {
    return <div className="verse-display empty">Sélectionnez un niveau et cliquez sur "Générer un verset"</div>;
  }

  return (
    <div className="verse-display">
      {/* Afficher l'information d'examen si disponible */}
      {verse.examInfo?.questionNumber && (
        <div className="exam-info">
          <div className="exam-label">
            Question {verse.examInfo.questionNumber}/{verse.examInfo.totalQuestions}
          </div>
          <div className="exam-hizb">
            {UNIT_LABELS[verse.examInfo.selectionType] || 'Unité'} testé{verse.examInfo.selectionType === 'surah' ? 'e' : ''}
            {' : '}
            {verse.examInfo.selectedValue}
          </div>
        </div>
      )}
      
      <div className="verse-reference">
        Sourate {displaySurah}, Verset {displayAyah}
      </div>

      {verse.audioUrl && (
        <div className="verse-audio-help">
          <button
            type="button"
            className={`verse-audio-button${isPlaying ? ' is-playing' : ''}`}
            onClick={playVerseAudio}
            title={isPlaying ? 'Écoute en cours…' : 'Écouter la récitation du verset'}
            aria-label={isPlaying ? 'Écoute en cours' : 'Écouter la récitation du verset'}
          >
            <ReciterIcon />
          </button>
          <audio
            ref={audioRef}
            src={verse.audioUrl}
            preload="none"
            onEnded={() => setIsPlaying(false)}
            onPause={() => setIsPlaying(false)}
          />
        </div>
      )}

      <div className={`verse-arabic${recitationMasked ? ' progressive-recitation' : ''}`}>
        {progressiveWords
          ? progressiveWords.map((word) => {
            // `display_status` temporise les erreurs non confirmées ; on suit la
            // même règle que la correction mot à mot pour éviter deux verdicts
            // divergents à l'écran.
            const status = word.display_status || word.status;
            // Un mot ne se dévoile qu'une fois récité correctement. Le CSS ne
            // fait que griser, il faut donc masquer le texte lui-même — sinon
            // le verset se lit intégralement avant d'avoir été récité.
            const shown = revealedSet ? revealedSet.has(word.index) : status === 'correct';
            return (
              <span
                key={`${word.index}-${word.text}`}
                className={`progressive-word progressive-${status}${shown ? '' : ' progressive-hidden'}`}
                title={status === 'pending' ? 'Mot à réciter' : status}
                aria-label={shown ? word.text : 'Mot à réciter'}
              >
                {shown ? word.text : PENDING_MASK}
              </span>
            );
          })
          : displayArabic}
      </div>
      
      {verse.transliteration && (
        <div className="verse-transliteration">
          <h3>Translittération:</h3>
          {verse.transliteration}
        </div>
      )}
      
      <div className="translation-accordion">
        <div 
          className="translation-header" 
          onClick={toggleTranslation}
        >
          <span>Traduction</span>
          <span className="accordion-icon">{showTranslation ? '▼' : '▶'}</span>
        </div>
        
        {showTranslation && (
          <div className="verse-translation">
            {verse.translation}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerseDisplay;
